import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getRiskConfigCoverage } from '@Actions';
import { selectCoverageEntries } from '@Selectors/RiskCoverage.selectors';
import { Category, Component, ComponentType } from '@Types';
import {
	hasCoverageToShow,
	hasSameCoverageSource,
	isHighWeightLowCoverage,
} from './componentCoverage';

// Loads per-component coverage for the LoF/CoF setup screens. Coverage is
// fetched once per screen (not per category) so the rows can show real data.
export const useLoadCoverage = (
	configId: string | undefined,
	enabled: boolean,
) => {
	const dispatch = useDispatch();
	useEffect(() => {
		if (enabled && configId) {
			dispatch(getRiskConfigCoverage(configId));
		}
	}, [enabled, configId, dispatch]);
};

// Returns, per component, the row's coverage pill data and thin-data warning.
// Presentation only: both are derived from the type and the coverage entry,
// never from where the component came from (template / user-added / scratch).
export const useComponentCoverage = (
	category: Category | undefined,
	enabled: boolean,
	// The same category as last loaded from the server. Coverage describes this
	// saved copy, so a component whose source the user has since changed (not
	// yet saved) gets no pill or warning rather than the old source's numbers.
	savedCategory?: Category,
) => {
	const entries = useSelector(selectCoverageEntries);
	return (comp: Component) => {
		const entry = entries.find(
			e => e.categoryName === category?.name && e.name === comp.name,
		);
		const saved = savedCategory?.components?.find(
			c => c.name === comp.name,
		);
		if (
			!enabled ||
			!hasSameCoverageSource(comp, saved) ||
			entry?.pct == null ||
			!hasCoverageToShow(comp.type as ComponentType, entry)
		) {
			return { coverage: undefined, thinDataWarning: false };
		}
		return {
			coverage: { pct: entry.pct, assets: entry.assets },
			thinDataWarning: isHighWeightLowCoverage(
				comp.weighting,
				comp.select,
				entry.pct,
			),
		};
	};
};
